'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useId, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { CheckGlyph, ClockGlyph } from '../../../internal/icons.js';
import { Button } from '../../../components/actions/button/index.js';
import { CopyField } from '../../../components/actions/copy-field/index.js';
import { Badge } from '../../../components/data-display/badge/index.js';
import { Alert } from '../../../components/feedback/alert/index.js';
import { Spinner } from '../../../components/feedback/spinner/index.js';
import { Field, FieldDescription, FieldError, FieldLabel, NativeSelect, } from '../../../components/forms/form-field/index.js';
import { Input } from '../../../components/forms/text-field/index.js';
import { ConfirmDialog } from '../../../components/overlays/confirm-dialog/index.js';
import { Stepper } from '../../../components/workflow/stepper/index.js';
const DEFAULT_MESSAGES = {
    stepsLabel: (domain) => (domain ? `Setup of ${domain}` : 'Custom domain setup'),
    steps: ['Domain', 'DNS records', 'Verify', 'Live'],
    domainLabel: 'Domain',
    placeholder: 'help.example.com',
    connect: 'Connect domain',
    errors: {
        empty: 'Enter a domain.',
        invalid: 'Enter a domain such as help.example.com, without https:// or a path.',
        tooLong: 'That domain is too long. Domains are at most 253 characters.',
        needsSubdomain: 'Use a subdomain, such as help.example.com.',
    },
    connectFailed: 'The domain could not be connected. Try again.',
    statusLabels: {
        pending: 'Waiting for DNS',
        verifying: 'Verifying',
        active: 'Active',
        failed: 'Failed',
    },
    statusText: {
        pending: 'Add the DNS records below at your DNS provider, then verify.',
        verifying: 'Checking your DNS records.',
        active: 'Your domain is connected.',
        failed: 'Verification failed.',
    },
    recordsHeading: 'DNS records',
    recordsIntro: 'Add these records where the DNS for your domain is managed.',
    type: 'Type',
    name: 'Name',
    value: 'Value',
    ttl: 'TTL',
    recordNameTarget: (r, i) => `${r.type} record ${i + 1} name`,
    recordValueTarget: (r, i) => `${r.type} record ${i + 1} value`,
    found: 'Found',
    notFound: 'Not found yet',
    providerLabel: 'Where is your DNS managed?',
    providerPlaceholder: 'Choose a provider',
    verify: 'Verify DNS',
    verifyAgain: 'Check again',
    verifyFailed: 'The records could not be checked. Try again.',
    remove: 'Remove domain',
    removeTitle: (d) => `Remove ${d}?`,
    removeDescription: 'The domain stops working here. You can connect it again later.',
    removeConfirm: 'Remove domain',
    cancel: 'Cancel',
};
const STATUS_TONE = {
    pending: 'warning',
    verifying: 'info',
    active: 'success',
    failed: 'danger',
};
const LABEL = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const TLD = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/;
/**
 * Turns what people paste into a bare host: trims, lowercases, drops a
 * scheme, path, query, port and trailing dot, and converts an international
 * name to its ASCII (punycode) form. Returns the text unchanged in shape
 * when it cannot be parsed, so `domainSyntaxError` can reject it.
 */
export function normalizeDomain(input) {
    let host = input.trim().toLowerCase();
    host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, '');
    host = host.replace(/[/?#].*$/, '');
    host = host.replace(/:\d*$/, '');
    host = host.replace(/\.$/, '');
    if ([...host].some((c) => c.charCodeAt(0) > 127)) {
        try {
            host = new URL(`http://${host}`).hostname;
        }
        catch {
            // Leave it; the syntax check reports it.
        }
    }
    return host;
}
/**
 * Checks domain syntax on the client. Returns `null` for a valid host name or
 * the reason it is not. `requireSubdomain` asks for at least three labels
 * (`help.example.com`); it cannot know public suffixes such as `co.uk`, so pass
 * a product `validate` for exact rules.
 */
export function domainSyntaxError(input, options = {}) {
    const host = normalizeDomain(input);
    if (!host)
        return 'empty';
    if (host.length > 253)
        return 'tooLong';
    const labels = host.split('.');
    if (labels.length < 2)
        return 'invalid';
    if (!labels.every((l) => LABEL.test(l)))
        return 'invalid';
    if (!TLD.test(labels[labels.length - 1]))
        return 'invalid';
    if (options.requireSubdomain && labels.length < 3)
        return 'needsSubdomain';
    return null;
}
function messageOf(error, fallback) {
    return error instanceof Error && error.message ? error.message : fallback;
}
/**
 * Connects a custom domain in four steps: enter the domain (syntax checked in
 * the browser), copy the DNS records, verify, and see the result: pending,
 * verifying, active, or failed with a reason. The product runs every DNS
 * check through `onConnect`, `onVerify` and `onRemove`, and supplies any
 * provider-specific instructions.
 */
export const CustomDomainConnect = forwardRef(function CustomDomainConnect({ domain: domainProp, defaultDomain = null, onDomainChange, onConnect, onVerify, onRemove, status: statusProp = 'pending', failureReason, records = [], showRecordsWhenActive = false, providerHints, lastChecked, inputDescription, requireSubdomain = false, validate, disabled = false, headingLevel = 3, messages: messagesProp, className, children, ...rest }, ref) {
    const messages = {
        ...DEFAULT_MESSAGES,
        ...messagesProp,
        errors: { ...DEFAULT_MESSAGES.errors, ...messagesProp?.errors },
        statusLabels: { ...DEFAULT_MESSAGES.statusLabels, ...messagesProp?.statusLabels },
        statusText: { ...DEFAULT_MESSAGES.statusText, ...messagesProp?.statusText },
    };
    const [innerDomain, setInnerDomain] = useState(defaultDomain);
    const controlled = domainProp !== undefined;
    const domain = controlled ? domainProp : innerDomain;
    const setDomain = useCallback((next) => {
        if (!controlled)
            setInnerDomain(next);
        onDomainChange?.(next);
    }, [controlled, onDomainChange]);
    const [text, setText] = useState('');
    const [inputError, setInputError] = useState(null);
    const [connecting, setConnecting] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [verifyError, setVerifyError] = useState(null);
    const [provider, setProvider] = useState('');
    const headingRef = useRef(null);
    const inputRef = useRef(null);
    const focusAfter = useRef(null);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);
    // Move focus to what replaced the control the person used.
    useEffect(() => {
        const target = focusAfter.current;
        focusAfter.current = null;
        if (target === 'heading')
            headingRef.current?.focus();
        else if (target === 'input')
            inputRef.current?.focus();
    }, [domain]);
    const auto = useId();
    const providerId = `${auto}-provider`;
    const Heading = `h${headingLevel}`;
    const status = verifying && statusProp !== 'active' ? 'verifying' : statusProp;
    const submit = async (event) => {
        event.preventDefault();
        if (connecting || disabled)
            return;
        const code = domainSyntaxError(text, { requireSubdomain });
        if (code) {
            setInputError(messages.errors[code]);
            inputRef.current?.focus();
            return;
        }
        const host = normalizeDomain(text);
        const custom = validate?.(host);
        if (custom) {
            setInputError(custom);
            inputRef.current?.focus();
            return;
        }
        setInputError(null);
        setConnecting(true);
        try {
            await onConnect(host);
            if (!mounted.current)
                return;
            focusAfter.current = 'heading';
            setText('');
            setVerifyError(null);
            setDomain(host);
        }
        catch (error) {
            if (!mounted.current)
                return;
            setInputError(messageOf(error, messages.connectFailed));
            inputRef.current?.focus();
        }
        finally {
            if (mounted.current)
                setConnecting(false);
        }
    };
    const verify = async () => {
        if (!onVerify || verifying || disabled)
            return;
        setVerifyError(null);
        setVerifying(true);
        try {
            await onVerify();
        }
        catch (error) {
            if (mounted.current)
                setVerifyError(messageOf(error, messages.verifyFailed));
        }
        finally {
            if (mounted.current)
                setVerifying(false);
        }
    };
    const remove = async () => {
        await onRemove?.();
        if (!mounted.current)
            return;
        focusAfter.current = 'input';
        setVerifyError(null);
        setProvider('');
        setDomain(null);
    };
    const stepIndex = !domain ? 0 : status === 'pending' ? 1 : 2;
    const showRecords = domain && records.length > 0 && (status !== 'active' || showRecordsWhenActive);
    const hint = providerHints?.find((p) => p.id === provider);
    return (_jsxs("section", { ref: ref, ...rest, className: cx('mn-custom-domain-connect', className), "data-step": !domain ? 'enter' : status, children: [status !== 'active' || !domain ? (_jsx(Stepper, { "aria-label": messages.stepsLabel(domain), className: "mn-custom-domain-connect__steps", current: stepIndex, steps: messages.steps.map((label, i) => ({
                    id: String(i),
                    label,
                    ...(i === 2 && domain && status === 'failed' ? { status: 'error' } : {}),
                })) })) : null, !domain ? (_jsx("form", { className: "mn-custom-domain-connect__form", noValidate: true, onSubmit: submit, children: _jsxs(Field, { invalid: Boolean(inputError), disabled: disabled, required: true, children: [_jsx(FieldLabel, { children: messages.domainLabel }), inputDescription ? _jsx(FieldDescription, { children: inputDescription }) : null, _jsxs("div", { className: "mn-custom-domain-connect__entry", children: [_jsx(Input, { ref: inputRef, value: text, onChange: (e) => {
                                        setText(e.target.value);
                                        if (inputError)
                                            setInputError(null);
                                    }, placeholder: messages.placeholder, inputMode: "url", autoComplete: "off", autoCapitalize: "none", spellCheck: false, className: "mn-custom-domain-connect__input" }), _jsx(Button, { type: "submit", variant: "primary", loading: connecting, disabled: disabled, className: "mn-custom-domain-connect__connect", children: messages.connect })] }), _jsx(FieldError, { children: inputError })] }) })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mn-custom-domain-connect__summary", children: [_jsxs("div", { className: "mn-custom-domain-connect__identity", children: [_jsx(Heading, { ref: headingRef, tabIndex: -1, className: "mn-custom-domain-connect__domain", children: domain }), _jsx(Badge, { tone: STATUS_TONE[status], dot: true, className: "mn-custom-domain-connect__badge", "data-status": status, children: messages.statusLabels[status] })] }), onRemove ? (_jsx(ConfirmDialog, { tone: "danger", title: messages.removeTitle(domain), description: messages.removeDescription, confirmLabel: messages.removeConfirm, cancelLabel: messages.cancel, onConfirm: remove, trigger: _jsx(Button, { size: "sm", variant: "ghost", disabled: disabled, className: "mn-custom-domain-connect__remove", children: messages.remove }) })) : null] }), _jsxs("div", { className: "mn-custom-domain-connect__status", children: [_jsxs("p", { role: "status", "aria-live": "polite", className: "mn-custom-domain-connect__status-text", children: [status === 'verifying' ? _jsx(Spinner, { size: "sm" }) : null, status === 'active' ? (_jsx("span", { className: "mn-custom-domain-connect__status-icon", "aria-hidden": true, children: _jsx(CheckGlyph, {}) })) : null, _jsx("span", { children: messages.statusText[status] })] }), lastChecked ? (_jsxs("p", { className: "mn-custom-domain-connect__checked", children: [_jsx("span", { className: "mn-custom-domain-connect__status-icon", "aria-hidden": true, children: _jsx(ClockGlyph, {}) }), lastChecked] })) : null] }), status === 'failed' && failureReason ? (_jsx(Alert, { tone: "danger", live: "assertive", className: "mn-custom-domain-connect__failure", children: failureReason })) : null, verifyError ? (_jsx(Alert, { tone: "danger", live: "assertive", className: "mn-custom-domain-connect__failure", children: verifyError })) : null, showRecords ? (_jsxs("div", { className: "mn-custom-domain-connect__records", children: [_jsx(Heading, { className: "mn-custom-domain-connect__heading", children: messages.recordsHeading }), _jsx("p", { className: "mn-custom-domain-connect__intro", children: messages.recordsIntro }), _jsx("ul", { className: "mn-custom-domain-connect__list", "aria-label": messages.recordsHeading, children: records.map((record, index) => (_jsxs("li", { className: "mn-custom-domain-connect__record", "data-found": record.found === undefined ? undefined : record.found ? 'true' : 'false', children: [_jsxs("div", { className: "mn-custom-domain-connect__record-head", children: [_jsxs("span", { className: "mn-custom-domain-connect__type", children: [_jsxs("span", { className: "mn-visually-hidden", children: [messages.type, ": "] }), record.type] }), record.purpose ? (_jsx("span", { className: "mn-custom-domain-connect__purpose", children: record.purpose })) : null, record.found !== undefined ? (_jsx(Badge, { tone: record.found ? 'success' : 'neutral', size: "sm", className: "mn-custom-domain-connect__found", children: record.found ? messages.found : messages.notFound })) : null] }), _jsxs(Field, { className: "mn-custom-domain-connect__value", children: [_jsx(FieldLabel, { children: messages.name }), _jsx(CopyField, { size: "sm", iconOnly: true, value: record.name, label: messages.recordNameTarget(record, index) })] }), _jsxs(Field, { className: "mn-custom-domain-connect__value", children: [_jsx(FieldLabel, { children: messages.value }), _jsx(CopyField, { size: "sm", iconOnly: true, value: record.value, label: messages.recordValueTarget(record, index) })] }), record.ttl !== undefined ? (_jsxs("p", { className: "mn-custom-domain-connect__ttl", children: [messages.ttl, ": ", record.ttl] })) : null] }, `${record.type}-${record.name}-${index}`))) })] })) : null, showRecords && providerHints && providerHints.length > 0 ? (_jsxs("div", { className: "mn-custom-domain-connect__provider", children: [_jsx("label", { htmlFor: providerId, className: "mn-custom-domain-connect__provider-label", children: messages.providerLabel }), _jsx(NativeSelect, { id: providerId, size: "sm", value: provider, placeholder: messages.providerPlaceholder, options: providerHints.map((p) => ({ value: p.id, label: p.label })), onChange: (e) => setProvider(e.target.value), wrapperClassName: "mn-custom-domain-connect__provider-select" }), hint ? (_jsx("ol", { className: "mn-custom-domain-connect__provider-steps", "aria-label": hint.label, children: hint.steps.map((step, i) => (_jsx("li", { children: step }, i))) })) : null] })) : null, children, onVerify && status !== 'active' ? (_jsx("div", { className: "mn-custom-domain-connect__actions", children: _jsx(Button, { variant: "primary", loading: status === 'verifying', disabled: disabled, onClick: verify, children: status === 'failed' ? messages.verifyAgain : messages.verify }) })) : null] }))] }));
});
//# sourceMappingURL=custom-domain-connect.js.map